import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography, 
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import './DataTable.css';
import CommonPopup from '../Popup/Popup';


type ColumnType = {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  isAvatarText?: boolean;
  isButton?: boolean;
  buttonTextColor?: string;
  buttonBgColor?: string;
  isIcon?: boolean; 
};

type DataTableProps<T> = {
  columns: ColumnType[];
  rows: T[];
  headerBgColor?: string;
  bodyBgColor?: string;
  paginationBgColor?: string;
  count?: number; 
  rowsPerPage?: number; 
  handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangePage?: (event: unknown, newPage: number) => void;
  page?: number;
};

export default function DataTable<T extends { [key: string]: any }>(props: DataTableProps<T>) {
  const {
    columns,
    rows,
    headerBgColor = '#252844',
    bodyBgColor = '',
    paginationBgColor = '#252844',
    count,
    rowsPerPage,
    handleChangeRowsPerPage,
    handleChangePage,
    page,
  } = props;

  // Internal state for client-side pagination if not controlled
  const [internalPage, setInternalPage] = React.useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = React.useState(10);

  // Popup state for image preview
  const [imagePopupUrl, setImagePopupUrl] = React.useState<string | any>(null);

  // Use controlled or uncontrolled pagination
  const currentPage = typeof page === 'number' ? page : internalPage;
  const currentRowsPerPage = typeof rowsPerPage === 'number' ? rowsPerPage : internalRowsPerPage;
  const totalCount = typeof count === 'number' ? count : rows.length;

  const onPageChange = handleChangePage || ((event: unknown, newPage: number) => setInternalPage(newPage));
  const onRowsPerPageChange = handleChangeRowsPerPage || ((event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalRowsPerPage(+event.target.value);
    setInternalPage(0);
  });

  // Only slice for client-side pagination
  const visibleRows = handleChangePage ? rows : rows.slice(
    currentPage * currentRowsPerPage,
    currentPage * currentRowsPerPage + currentRowsPerPage
  );

  const handleDownload = (row: T) => {
    const url = row.receiptUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderCellContent = (
    value: any,
    column: ColumnType,
    row: T
  ): React.ReactNode => {
    if (column.isAvatarText) {
      // If value is an object with url and fileName
      if (value && typeof value === 'object' && value.url) {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar src={value.url} style={{ cursor: 'pointer' }} onClick={() => setImagePopupUrl(value)} />
            <Typography variant="body2" color="#fff" sx={{ ml: 1, wordBreak: 'break-all', cursor: 'pointer' }} onClick={() => setImagePopupUrl(value)}>
              {value.fileName}
            </Typography>
          </Box>
        );
      }
      // If value is just a string (url)
      if (typeof value === 'string') {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar src={value} style={{ cursor: 'pointer' }} onClick={() => setImagePopupUrl(value)} />
          </Box>
        );
      }
    }

    if (column.isButton) {
      if (!value) return null;

      // Normalize status values for display and color
      let displayValue = value;
      let buttonColor = column.buttonTextColor || '#fff';
      let buttonBorder = `1px solid ${column.buttonBgColor}` || '#1976d2';

      // Normalize status for display
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'paid' || value === 'PAYMENT_SUCCESS') {
          displayValue = 'Paid';
          buttonColor = '#54E324';
          buttonBorder = '1px solid #54E324';
        } else if (value.toLowerCase() === 'pending') {
          displayValue = 'Pending';
          buttonColor = '#FFC107';
          buttonBorder = '1px solid #FFC107';
        } else if (
          value === 'Payment_Error' ||
          value === 'PAYMENT_ERROR' ||
          value === 'unpaid' ||
          value === 'Failed'
        ) {
          displayValue = 'Failed';
          buttonColor = '#FF3B30';
          buttonBorder = '1px solid #FF3B30';
        }
      }

      // Action button logic (for download)
      if (column.id === 'action') {
        const hasReceipt = !!row.receiptUrl;
        const isStripeReceipt = hasReceipt && row.receiptUrl.startsWith('https://pay.stripe.com/receipts/payment/');
        if (!isStripeReceipt) return null;
        return (
          <Button
            sx={{
              color: '#FFFFFF',
              border: '1px solid #FFFFFF',
              textTransform: 'none',
              opacity: hasReceipt ? 1 : 0.5,
              cursor: hasReceipt ? 'pointer' : 'not-allowed',
            }}
            className='dataTableButton'
            size="small"
            startIcon={<FileDownloadIcon />}
            disabled={!hasReceipt}
            onClick={() => hasReceipt && handleDownload(row)}
            title={hasReceipt ? 'Download receipt' : 'No receipt available'}
          >
            Download
          </Button>
        );
      }

      return (
        <Button
          sx={{
            color: buttonColor,
            border: buttonBorder,
            textTransform: 'none',
          }}
          className='dataTableButton'
          size="small"
        >
          {column.isIcon ? <FileDownloadIcon /> : null}
          {typeof displayValue === 'string' ? displayValue : 'Click'}
        </Button>
      );
    }

    return typeof value === 'string' || typeof value === 'number'
      ? value
      : value
      ? JSON.stringify(value)
      : null;
  };

  return (
    <>
      <Paper
        sx={{
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 320px)',
            overflow: 'auto',
            border: '1px solid #343761',
            borderRadius: '10px 0px',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      backgroundColor: headerBgColor,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      minWidth: '150px',
                      color: '#A1ADC1',
                      fontFamily: 'var(--font-family-primary)',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody sx={{ backgroundColor: bodyBgColor }}>
              { visibleRows.length>0 ? visibleRows.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      sx={{
                        borderBottom: '1px solid #7377AD',
                        minWidth: '150px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#FFFFFF',
                        fontFamily: 'var(--font-family-primary)',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {renderCellContent(row[column.id], column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              )):(
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{
                        minWidth: '150px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#FFFFFF',
                        fontFamily: 'var(--font-family-primary)',
                        fontSize: '14px',
                        fontWeight: 400,
                        height: 400,
                      }}>
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          sx={{ backgroundColor: paginationBgColor,  
            marginTop: '5px',
             '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        color: '#FFFFFF',
        fontFamily: 'var(--font-family-primary)',
        fontSize: '14px',
        fontWeight: 400,
      },
          }}
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={currentRowsPerPage}
          page={currentPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>
      {/* Image popup */}
      {imagePopupUrl && (
       <CommonPopup
       leftImageSrc={imagePopupUrl.url || ''}
       leftColumnType='prompt'
       open={!!imagePopupUrl}
       onClose={() => setImagePopupUrl(null)}
       previewImageSrc={imagePopupUrl.url || ''}
        leftPromptText={imagePopupUrl.prompt || ''}
        rightColumnType='image'
       />
      )}
    </>
  );
}
